import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { DropDownFilter, Loading, ToolTip } from './utility/Utility';

const BRIEF_STATUSES = ['new', 'saved', 'in_progress', 'launched', 'dismissed'];

export function badgeLabel(badgeType) {
  if (badgeType === 'top') return 'High confidence';
  if (badgeType === 'combo') return 'New combo';
  return 'Catalog gap';
}

export function badgeClass(badgeType) {
  if (badgeType === 'top') return 'product-brief-badge product-brief-badge--top';
  if (badgeType === 'combo') return 'product-brief-badge product-brief-badge--combo';
  return 'product-brief-badge product-brief-badge--gap';
}

export function confidenceFillClass(pct) {
  if (pct >= 66) return 'product-brief-confidence__fill product-brief-confidence__fill--hi';
  if (pct >= 33) return 'product-brief-confidence__fill product-brief-confidence__fill--mid';
  return 'product-brief-confidence__fill product-brief-confidence__fill--lo';
}

export function confidencePercent(confidence) {
  return Math.round(Math.min(100, Math.max(0, Number(confidence) || 0)));
}

export function formatBriefDate(d) {
  if (!d) return '';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  return dt.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

export function InsightsBanner({ insights }) {
  if (!insights || (insights.briefs_generated == null && !insights.generated_at)) return null;
  const dr = insights.date_range;
  const rangeLabel =
    dr && dr.start && dr.end
      ? `${formatBriefDate(dr.start)} – ${formatBriefDate(dr.end)}`
      : '';
  return (
    <div className="product-briefs__banner">
      <div className="product-briefs__chips">
        <span className="product-briefs__chip">Products analyzed: {insights.products_analyzed ?? '—'}</span>
        <span className="product-briefs__chip">Briefs generated (last run): {insights.briefs_generated ?? 0}</span>
        {insights.briefs_skipped_duplicate != null && (
          <span className="product-briefs__chip">Skipped duplicate: {insights.briefs_skipped_duplicate}</span>
        )}
        {rangeLabel && <span className="product-briefs__chip">Range: {rangeLabel}</span>}
      </div>
    </div>
  );
}

const STATUS_FILTER_OPTS = [
  { value: 'all', label: 'All' },
  { value: 'new', label: 'New' },
  { value: 'saved', label: 'Saved' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'launched', label: 'Launched' },
  { value: 'dismissed', label: 'Dismissed' }
];

const SORT_FILTER_OPTS = [
  { value: 'newest', label: 'Newest' },
  { value: 'confidence', label: 'Confidence' },
  { value: 'product_type', label: 'Product type A–Z' }
];

export function BriefStatusFilter({ status, setStatus }) {
  const options = useMemo(
    () =>
      STATUS_FILTER_OPTS.map((o) => ({
        text: o.label,
        action: () => setStatus(o.value)
      })),
    [setStatus]
  );
  const active = STATUS_FILTER_OPTS.find((o) => o.value === status)?.label ?? 'All';
  const setActive = (label) => {
    const o = STATUS_FILTER_OPTS.find((x) => x.label === label);
    if (o) setStatus(o.value);
  };
  return (
    <div className="product-briefs-filter-field">
      <div className="product-briefs-filter-field__label">Status</div>
      <DropDownFilter className="product-briefs-dropdown" options={options} active={active} setActive={setActive} />
    </div>
  );
}

export function BriefSortFilter({ sort, setSort }) {
  const options = useMemo(
    () =>
      SORT_FILTER_OPTS.map((o) => ({
        text: o.label,
        action: () => setSort(o.value)
      })),
    [setSort]
  );
  const active = SORT_FILTER_OPTS.find((o) => o.value === sort)?.label ?? 'Newest';
  const setActive = (label) => {
    const o = SORT_FILTER_OPTS.find((x) => x.label === label);
    if (o) setSort(o.value);
  };
  return (
    <div className="product-briefs-filter-field">
      <div className="product-briefs-filter-field__label">Sort</div>
      <DropDownFilter className="product-briefs-dropdown" options={options} active={active} setActive={setActive} />
    </div>
  );
}

/**
 * Dismissible pills for active generate-page filter selections.
 * @param {{ items: { id: string, label: string, group?: string }[], onRemove: (id: string, group?: string) => void, className?: string }} props
 */
export function SelectedFilterChips({ items, onRemove, className = '' }) {
  if (!items?.length) return null;
  return (
    <div className={`product-briefs-generate__chips ${className}`.trim()} role="list" aria-label="Active filters">
      {items.map((item) => (
        <span
          key={`${item.group || 'x'}-${item.id}`}
          className="product-briefs__chip product-briefs__chip--filter"
          role="listitem"
        >
          {item.group ? <span className="product-briefs__chip-group">{item.group}: </span> : null}
          <span className="product-briefs__chip-label">{item.label}</span>
          <button
            type="button"
            className="product-briefs__chip-remove"
            aria-label={`Remove ${item.label}`}
            onClick={() => onRemove(item.id, item.group)}
          >
            ×
          </button>
        </span>
      ))}
    </div>
  );
}

function formatPreviewRevenue(n) {
  const v = Math.round(Number(n) || 0);
  return v.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

function BriefGeneratePreviewThumb({ product }) {
  const title = product?.title || 'Untitled';
  const alt = product?.primary_image_alt || title;
  return (
    <article className="product-briefs-generate-preview__card">
      <div className="product-briefs-generate-preview__img-wrap">
        {product?.primary_image_src ? (
          <img src={product.primary_image_src} alt={alt} loading="lazy" />
        ) : (
          <span className="product-briefs-generate-preview__placeholder" aria-hidden />
        )}
      </div>
      <p className="product-briefs-generate-preview__title" title={title}>
        {title}
      </p>
      <p className="product-briefs-generate-preview__meta">{formatPreviewRevenue(product?.net_revenue)} net</p>
    </article>
  );
}

function BriefGeneratePreviewGrid({ products }) {
  if (!products?.length) {
    return <p className="product-briefs__muted product-briefs-generate-preview__empty">No qualifying products.</p>;
  }
  return (
    <div className="product-briefs-generate-preview__grid">
      {products.map((p) => (
        <BriefGeneratePreviewThumb key={p.id} product={p} />
      ))}
    </div>
  );
}

export function BriefSourceProductsGrid({ products }) {
  return <BriefGeneratePreviewGrid products={products} />;
}

/**
 * Persisted source_products on a saved brief (same thumb grid as generate preview).
 */
export function BriefSourceProductsPanel({ brief }) {
  const products = brief?.source_products;
  if (!products?.length) return null;
  const basis = brief?.generation_basis;
  const mode = basis?.mode;
  const matching = basis?.matching_count;
  const title =
    mode === 'products'
      ? 'Based on selected products'
      : 'Based on top performers';
  return (
    <div className="product-brief-detail__section product-briefs-generate-preview">
      <h3>{title}</h3>
      {matching != null && (
        <p className="product-briefs__muted product-briefs-generate-preview__sub">
          {mode === 'products' ? (
            <>
              {products.length} product{products.length === 1 ? '' : 's'} analyzed
              {matching !== products.length ? ` (${matching} matched filters)` : ''}.
            </>
          ) : (
            <>
              Top {products.length} of <strong>{matching}</strong> matching products in the sales window.
            </>
          )}
        </p>
      )}
      <BriefSourceProductsGrid products={products} />
    </div>
  );
}

/**
 * Shows products that will feed brief generation (top performers or selected products).
 */
export function BriefGeneratePreview({
  preview,
  loading,
  error,
  mode,
  topN = 20
}) {
  if (mode === 'products' && !preview && !loading && !error) {
    return (
      <p className="product-briefs__muted product-briefs-generate-preview__hint">
        Select products above to preview what will be analyzed.
      </p>
    );
  }

  if (loading) {
    return (
      <div className="product-briefs-generate-preview">
        <p className="product-briefs__muted">Loading preview…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="product-briefs-generate-preview" role="alert">
        <p className="product-briefs__error">{error}</p>
      </div>
    );
  }

  if (!preview?.success) return null;

  if (mode === 'products') {
    return (
      <div className="product-briefs-generate-preview">
        <div className="product-briefs-generate-preview__head">
          <h3 className="product-briefs-generate-preview__heading">Preview</h3>
          <span className="product-briefs-generate-preview__count">
            Matching products: <strong>{preview.matching_count ?? 0}</strong>
            {preview.requested_count != null && preview.requested_count !== preview.matching_count ? (
              <span className="product-briefs__muted">
                {' '}
                ({preview.requested_count} selected)
              </span>
            ) : null}
          </span>
        </div>
        {!preview.qualifies && (
          <p className="product-briefs-generate-preview__warn">
            At least one selected product needs net sales in the window and successful AI vision.
          </p>
        )}
        <p className="product-briefs__muted product-briefs-generate-preview__sub">
          These products will be used to generate the brief.
        </p>
        <BriefGeneratePreviewGrid products={preview.products} />
      </div>
    );
  }

  return (
    <div className="product-briefs-generate-preview">
      <div className="product-briefs-generate-preview__head">
        <h3 className="product-briefs-generate-preview__heading">Preview</h3>
        <span className="product-briefs-generate-preview__count">
          Matching products: <strong>{preview.matching_count ?? 0}</strong>
        </span>
      </div>
      {!preview.qualifies && (
        <p className="product-briefs-generate-preview__warn">
          Need at least {preview.min_required ?? 10} matching products with sales and AI vision in this range to
          generate a brief.
        </p>
      )}
      <p className="product-briefs__muted product-briefs-generate-preview__sub">
        Top {topN} performers below are saved with the brief and used as the primary sales reference set.
      </p>
      <section className="product-briefs-generate-preview__section" aria-labelledby="brief-preview-top-heading">
        <h4 id="brief-preview-top-heading" className="product-briefs-generate-preview__section-title">
          Top performers ({preview.products?.length ?? 0})
        </h4>
        <BriefGeneratePreviewGrid products={preview.products} />
      </section>
    </div>
  );
}

export function BriefProductTypeFilter({ types, productType, setProductType }) {
  const options = useMemo(() => {
    const rows = [{ text: 'All types', action: () => setProductType('') }];
    for (const t of [...types].sort((a, b) => a.localeCompare(b))) {
      rows.push({ text: t, action: () => setProductType(t) });
    }
    return rows;
  }, [types, setProductType]);
  const active = productType === '' ? 'All types' : productType;
  const setActive = (text) => {
    if (text === 'All types') setProductType('');
    else setProductType(text);
  };
  return (
    <div className="product-briefs-filter-field product-briefs-filter-field--grow">
      <div className="product-briefs-filter-field__label">Product type</div>
      <DropDownFilter
        className="product-briefs-dropdown product-briefs-dropdown--wide"
        options={options}
        active={active}
        setActive={setActive}
      />
    </div>
  );
}

function swatchHexLabel(hex, fallbackName) {
  if (hex && /^#/.test(String(hex).trim())) return String(hex).trim();
  if (fallbackName) return String(fallbackName);
  return '—';
}

function isPrimaryBriefPaletteSwatch(entry, index) {
  const r = Number(entry?.rank);
  if (Number.isFinite(r) && r === 1) return true;
  if (!Number.isFinite(r) && index === 0) return true;
  return false;
}

export function PaletteSwatches({ colorPalette, copyable = false }) {
  const palette = (colorPalette || []).slice(0, 5);
  const [copiedHex, setCopiedHex] = useState('');
  const copiedTimerRef = useRef(null);

  useEffect(
    () => () => {
      if (copiedTimerRef.current) window.clearTimeout(copiedTimerRef.current);
    },
    []
  );

  if (!palette.length) return null;

  const copyHex = async (hex, fallbackName) => {
    const value = swatchHexLabel(hex, fallbackName);
    if (!value || value === '—') return;
    try {
      await navigator.clipboard.writeText(value);
      setCopiedHex(value);
      if (copiedTimerRef.current) window.clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = window.setTimeout(() => setCopiedHex(''), 1200);
    } catch (_) {
      // Clipboard can fail in some browser contexts; no-op to keep UI simple.
    }
  };

  const swatchClass = (c, i) =>
    `product-brief-swatch${copyable ? ' product-analysis-detail__swatch-hit' : ''}${
      isPrimaryBriefPaletteSwatch(c, i) ? ' product-brief-swatch--base' : ''
    }`;

  return (
    <div
      className={`product-brief-swatches${copyable ? ' product-analysis-detail__swatches--copyable' : ''}`}
    >
      <span className="product-briefs__muted" style={{ fontSize: '0.7rem' }}>
        Colors
      </span>
      {palette.map((c, i) => {
        const label = swatchHexLabel(c.hex, c.name);
        const tooltipMessage = copiedHex && copiedHex === label ? 'Copied!' : label;
        const swatch = (
          <ToolTip
            className="product-brief-swatch-tooltip"
            message={tooltipMessage}
            trigger={
              <span
                className={swatchClass(c, i)}
                style={{ background: c.hex && /^#/.test(c.hex) ? c.hex : '#ddd' }}
              />
            }
          />
        );

        if (!copyable) {
          return <React.Fragment key={i}>{swatch}</React.Fragment>;
        }

        return (
          <button
            key={i}
            type="button"
            className="product-analysis-detail__swatch-copy-wrap"
            onClick={() => copyHex(c.hex, c.name)}
            aria-label={
              isPrimaryBriefPaletteSwatch(c, i)
                ? `Copy primary (rank 1) color hex ${label}`
                : `Copy palette color ${i + 1} hex ${label}`
            }
          >
            {swatch}
          </button>
        );
      })}
    </div>
  );
}

function briefThemes(brief) {
  if (Array.isArray(brief?.themes) && brief.themes.length) return brief.themes;
  return Array.isArray(brief?.mood) ? brief.mood : [];
}

function briefDesignStyles(brief) {
  if (Array.isArray(brief?.design_styles) && brief.design_styles.length) return brief.design_styles;
  if (brief?.design_style) return [brief.design_style];
  return [];
}

function AttributeTags({ subjects, themes, designStyles, max = 6 }) {
  const subj = subjects || [];
  const th = themes || [];
  const styles = designStyles || [];
  const combined = [
    ...subj.map((s) => ({ k: 's', v: s })),
    ...styles.map((d) => ({ k: 'd', v: d })),
    ...th.map((t) => ({ k: 't', v: t }))
  ];
  const shown = combined.slice(0, max);
  const more = combined.length - shown.length;
  return (
    <div className="product-brief-tags">
      {shown.map((x, i) => (
        <span key={`${x.k}-${i}`} className="product-brief-tag">
          {x.v}
        </span>
      ))}
      {more > 0 && <span className="product-brief-tag">+{more} more</span>}
    </div>
  );
}

export function ConfidenceBar({ confidence, variant = 'default' }) {
  const pct = confidencePercent(confidence);
  if (variant === 'barOnly') {
    return (
      <div
        className="product-brief-confidence product-brief-confidence--bar-only"
        role="meter"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}
        aria-label={`Confidence ${pct}%`}
      >
        <div className="product-brief-confidence__bar">
          <div className={confidenceFillClass(pct)} style={{ width: `${pct}%` }} />
        </div>
      </div>
    );
  }
  return (
    <div className="product-brief-confidence">
      <div className="product-brief-confidence__row">
        <span>Confidence</span>
        <span>{pct}%</span>
      </div>
      <div className="product-brief-confidence__bar">
        <div className={confidenceFillClass(pct)} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function briefStatusMenuLabel(status) {
  const s = status || 'new';
  if (s === 'in_progress') return 'In progress';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function StatusControl({ brief, onStatusChange, disabled, id }) {
  const current = brief.status || 'new';
  const active = briefStatusMenuLabel(current);
  const options = useMemo(
    () =>
      BRIEF_STATUSES.map((s) => ({
        text: briefStatusMenuLabel(s),
        action: () => onStatusChange(brief, s)
      })),
    [brief._id, brief.status, onStatusChange]
  );
  const setActive = (label) => {
    const match = BRIEF_STATUSES.find((s) => briefStatusMenuLabel(s) === label);
    if (match != null) onStatusChange(brief, match);
  };
  return (
    <DropDownFilter
      inputId={id}
      className="product-briefs-dropdown product-briefs-dropdown--status"
      options={options}
      active={active}
      setActive={setActive}
      disabled={disabled}
    />
  );
}

export function BriefPendingCard({ status, error, onDismiss }) {
  const statusLabel =
    status === 'queued' ? 'Queued' : status === 'running' ? 'Running' : 'Generating';
  if (error) {
    return (
      <article className="product-brief-card product-brief-card--pending product-brief-card--pending-error">
        <div className="product-brief-card__head">
          <div>
            <h3 className="product-brief-card__title">Generation failed</h3>
            <p className="product-brief-card__type product-briefs__error">{error}</p>
          </div>
        </div>
        {onDismiss && (
          <div className="product-brief-card__footer product-brief-card__footer--pending">
            <button type="button" className="product-briefs__btn product-briefs__btn--secondary" onClick={onDismiss}>
              Dismiss
            </button>
          </div>
        )}
      </article>
    );
  }
  return (
    <article
      className="product-brief-card product-brief-card--pending"
      aria-busy="true"
      aria-label="Product brief is generating"
    >
      <div className="product-brief-card__head">
        <div>
          <h3 className="product-brief-card__title product-brief-card__title--pending">Generating product brief</h3>
          <p className="product-brief-card__type">Status: {statusLabel} — usually takes about a minute</p>
        </div>
      </div>
      <div className="product-brief-pending__body">
        <Loading className="product-brief-pending__spinner" />
        <p className="product-briefs__muted">Analyzing products and writing your brief…</p>
      </div>
      <div className="product-brief-card__footer product-brief-card__footer--pending">
        <span className="product-briefs__btn product-briefs__btn--primary product-briefs__btn--disabled" aria-disabled="true">
          View brief
        </span>
      </div>
    </article>
  );
}

export function briefMockupReady(brief) {
  return brief?.mockup_image?.status === 'ready' && brief.mockup_image.url;
}

export function BriefMockupLightbox({ url, alt, onClose }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  if (!url) return null;

  return (
    <div className="product-brief-mockup-lightbox" role="dialog" aria-modal="true" aria-label="Enlarged product mockup">
      <button type="button" className="product-brief-mockup-lightbox__backdrop" onClick={onClose} aria-label="Close" />
      <div className="product-brief-mockup-lightbox__panel">
        <button type="button" className="product-brief-mockup-lightbox__close" onClick={onClose} aria-label="Close">
          ×
        </button>
        <img src={url} alt={alt} className="product-brief-mockup-lightbox__img" />
      </div>
    </div>
  );
}

export function BriefMockupHero({ brief, variant = 'detail', onRetryMockup, mockupRetryBusy }) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const mockup = brief?.mockup_image;
  const alt = `AI mockup for ${brief?.title || 'product brief'}`;

  if (briefMockupReady(brief)) {
    const enlargeable = variant === 'detail-inline';
    return (
      <>
        <div className={`product-brief-mockup product-brief-mockup--${variant}`}>
          {enlargeable ? (
            <button
              type="button"
              className="product-brief-mockup__enlarge-btn"
              onClick={() => setLightboxOpen(true)}
              aria-label="View enlarged mockup"
            >
              <img src={mockup.url} alt={alt} className="product-brief-mockup__img" loading="lazy" />
            </button>
          ) : (
            <img src={mockup.url} alt={alt} className="product-brief-mockup__img" loading="lazy" />
          )}
        </div>
        {lightboxOpen && (
          <BriefMockupLightbox url={mockup.url} alt={alt} onClose={() => setLightboxOpen(false)} />
        )}
      </>
    );
  }
  if (variant !== 'detail' && variant !== 'detail-inline') return null;
  if (!mockup?.status) return null;

  if (mockup.status === 'pending') {
    return (
      <div className="product-brief-mockup__status product-brief-mockup__status--pending">
        <p className="product-briefs__muted product-brief-mockup__status-msg">Generating mockup image…</p>
      </div>
    );
  }

  const canRetry =
    onRetryMockup && (mockup.status === 'error' || mockup.status === 'skipped');
  const msg =
    mockup.status === 'error'
      ? 'Mockup image could not be generated.'
      : mockup.status === 'skipped'
        ? 'Mockup image was not generated (not configured).'
        : null;
  if (!msg && !canRetry) return null;

  return (
    <div className="product-brief-mockup__status product-brief-mockup__status--error">
      {msg && <p className="product-briefs__muted product-brief-mockup__status-msg">{msg}</p>}
      {mockup.error && mockup.status === 'error' && (
        <p className="product-brief-mockup__error-detail">{mockup.error}</p>
      )}
      {canRetry && (
        <button
          type="button"
          className="product-briefs__btn product-briefs__btn--secondary product-brief-mockup__retry-btn"
          onClick={onRetryMockup}
          disabled={mockupRetryBusy}
        >
          {mockupRetryBusy ? 'Starting retry…' : 'Retry mockup image'}
        </button>
      )}
    </div>
  );
}

export function BriefCard({ brief, onStatusChange, statusBusyId }) {
  const st = brief.status || 'new';
  const hasMockup = briefMockupReady(brief);
  return (
    <article className={`product-brief-card product-brief-card--${st}`}>
      <div className="product-brief-card__body">
        {hasMockup && (
          <div className="product-brief-card__mockup">
            <BriefMockupHero brief={brief} variant="card" />
          </div>
        )}
        <div className="product-brief-card__main">
          <div className="product-brief-card__head">
            <div>
              <h3 className="product-brief-card__title">{brief.title || 'Untitled brief'}</h3>
              <p className="product-brief-card__type">{brief.product_type || '—'}</p>
            </div>
            <span className={badgeClass(brief.badge_type)}>{badgeLabel(brief.badge_type)}</span>
          </div>
          <PaletteSwatches colorPalette={brief.color_palette} copyable />
          <AttributeTags
            subjects={brief.subjects}
            designStyles={briefDesignStyles(brief)}
            themes={briefThemes(brief)}
          />
        </div>
      </div>
      <ConfidenceBar confidence={brief.confidence} />
      <div className="product-brief-card__footer">
        <StatusControl
          brief={brief}
          onStatusChange={onStatusChange}
          disabled={statusBusyId === brief._id}
        />
        <Link to={`/app/product-analysis/briefs/${brief._id}`} className="product-briefs__btn product-briefs__btn--primary">
          View brief
        </Link>
      </div>
    </article>
  );
}

export function BriefListEmpty({ onGenerate }) {
  return (
    <div className="product-briefs__empty">
      <h2>No product briefs yet</h2>
      <p>Run a constrained generation to get a data-backed product idea tailored to your catalog.</p>
      <button type="button" className="product-briefs__btn product-briefs__btn--primary" onClick={onGenerate}>
        Generate brief
      </button>
    </div>
  );
}

export function CopyDirectionBlock({ copy }) {
  if (!copy) return null;
  return (
    <div className="product-brief-detail__section">
      <h3>Copy direction</h3>
      <p>
        <strong>Name:</strong> {copy.product_name || '—'}
      </p>
      <p>
        <strong>Back text:</strong> {copy.back_text || '—'}
      </p>
      <p>
        <strong>Description angle:</strong> {copy.description_angle || '—'}
      </p>
    </div>
  );
}

export function ReferenceProductsList({ items }) {
  if (!items?.length) return null;
  return (
    <div className="product-brief-detail__section">
      <h3>Reference products</h3>
      <ul className="reference-products">
        {items.map((r, i) => (
          <li key={i}>
            <strong>{r.title}</strong>
            {r.net_revenue != null && <span className="product-briefs__muted"> — ${Number(r.net_revenue).toFixed(0)}</span>}
            {r.why_relevant && <div className="product-briefs__muted">{r.why_relevant}</div>}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function DataRationaleBlock({ text }) {
  if (!text) return null;
  return (
    <div className="product-brief-detail__section">
      <h3>Data rationale</h3>
      <p className="product-briefs__muted" style={{ whiteSpace: 'pre-wrap' }}>
        {text}
      </p>
    </div>
  );
}

export function CompositionBlock({ composition, printLocation }) {
  const locs = printLocation || [];
  return (
    <div className="product-brief-detail__section">
      <h3>Composition</h3>
      <div className="product-brief-detail__composition">{composition || '—'}</div>
      {locs.length > 0 && (
        <div className="product-brief-detail__tags-inline">
          {locs.map((p) => (
            <span key={p} className="product-brief-tag">
              {p}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

const SIGNAL_PREFIX_ORDER = ['subject', 'theme', 'color', 'style', 'type', 'composition', 'print_loc', 'other'];
const KNOWN_SIGNAL_PREFIXES = new Set(SIGNAL_PREFIX_ORDER.filter((p) => p !== 'other'));

const SIGNAL_GROUP_LABELS = {
  subject: 'Subject',
  theme: 'Theme',
  mood: 'Theme',
  color: 'Color',
  style: 'Design style',
  type: 'Product type',
  composition: 'Composition',
  print_loc: 'Print location',
  other: 'Other'
};

function parseConfidenceSignalKey(key) {
  const s = String(key || '');
  const i = s.indexOf(':');
  if (i <= 0) return { category: 'other', display: s || '—' };
  const prefix = s.slice(0, i);
  const rest = s.slice(i + 1);
  if (KNOWN_SIGNAL_PREFIXES.has(prefix)) return { category: prefix, display: rest || '—' };
  return { category: 'other', display: s };
}

function groupConfidenceSignals(signals) {
  const map = new Map();
  for (const sig of signals) {
    const { category, display } = parseConfidenceSignalKey(sig.key);
    if (!map.has(category)) map.set(category, []);
    map.get(category).push({ ...sig, display });
  }
  for (const arr of map.values()) {
    arr.sort((a, b) => (Number(b.lift) || 0) - (Number(a.lift) || 0));
  }
  return SIGNAL_PREFIX_ORDER.filter((c) => map.has(c)).map((c) => ({
    category: c,
    label: SIGNAL_GROUP_LABELS[c] || c,
    items: map.get(c)
  }));
}

export function ConfidenceSignalsChips({ signals }) {
  const groups = useMemo(() => (signals?.length ? groupConfidenceSignals(signals) : []), [signals]);
  if (!groups.length) return null;
  const liftTooltipMessage =
    'Each value shows how often that attribute appears among your top-selling designs vs. lower performers. The number after × is lift (ratio); higher values mean the catalog supports that signal more strongly for this brief.';

  return (
    <div className="product-brief-detail__section">
      <div className="product-brief-detail__section-heading">
        <h3>Confidence signals</h3>
        <ToolTip
          className="product-brief-confidence-signals-tip"
          message={liftTooltipMessage}
          width={270}
        />
      </div>
      <div className="confidence-signals-groups">
        {groups.map(({ category, label, items }) => (
          <div key={category} className="confidence-signals__group">
            <div className="confidence-signals__group-label">{label}</div>
            <div className="confidence-signals">
              {items.map((s, i) => (
                <span key={`${category}-${i}`}>
                  {s.display}
                  {s.lift != null ? ` ×${Number(s.lift).toFixed(2)}` : ''}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function FeedbackWidget({ brief, onSubmit, busy }) {
  const [rating, setRating] = useState(brief.feedback?.rating ?? '');
  const [useful, setUseful] = useState(
    typeof brief.feedback?.useful === 'boolean' ? brief.feedback.useful : ''
  );
  const [notes, setNotes] = useState(brief.feedback?.notes ?? '');
  useEffect(() => {
    setRating(brief.feedback?.rating ?? '');
    setUseful(typeof brief.feedback?.useful === 'boolean' ? brief.feedback.useful : '');
    setNotes(brief.feedback?.notes ?? '');
  }, [brief._id]);

  return (
    <div className="product-brief-detail__section">
      <h3>Feedback</h3>
      <form
        className="product-briefs__form"
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit({
            rating: rating === '' ? null : Number(rating),
            useful: useful === '' ? null : useful === true || useful === 'true',
            notes: notes || null
          });
        }}
      >
        <label>
          Rating (1–5)
          <input
            type="number"
            min={1}
            max={5}
            value={rating}
            onChange={(e) => setRating(e.target.value === '' ? '' : Number(e.target.value))}
          />
        </label>
        <label>
          Useful
          <select
            value={useful === true ? 'true' : useful === false ? 'false' : ''}
            onChange={(e) => {
              const v = e.target.value;
              setUseful(v === '' ? '' : v === 'true');
            }}
          >
            <option value="">—</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        </label>
        <label>
          Notes
          <input value={notes} onChange={(e) => setNotes(e.target.value)} />
        </label>
        <button type="submit" className="product-briefs__btn product-briefs__btn--primary" disabled={busy}>
          Save feedback
        </button>
      </form>
    </div>
  );
}
